import type { DeepOrder, OrderType, CancelStatus, ConfirmationStatus } from './types'
import i18n from '@/i18n'
import {
  getOrderStatusStyle,
  getPaymentStyle,
  getConfirmationStyle,
  getUrgencyClass,
  CARD_ACCENT,
  CARD_BG,
  ORDER_TYPE_STYLES,
} from '@/config/design-tokens'
import type { StatusStyle, PaymentStyle } from '@/config/design-tokens'

/**
 * Get order type from order flags
 */
export function getOrderType(order: DeepOrder): OrderType {
  if (order.dinein) return 'dineIn'
  if (order.withDelivery) return 'delivery'
  return 'pickup'
}

/**
 * Check if order is cancelled
 */
export function isCancelled(order: DeepOrder): boolean {
  return order.cancelStatus === 'cancelled'
}

/**
 * Check if order is a scheduled/future order
 */
export function isScheduledOrder(order: DeepOrder): boolean {
  return order.commitedTime !== null && order.commitedTime > Date.now()
}

/**
 * Get confirmation status color classes — from design tokens
 */
export function getConfirmationStatusColor(confirmationStatus: ConfirmationStatus) {
  return getConfirmationStyle(confirmationStatus)
}

/**
 * Get card background/border classes for kitchen ticket cards based on confirmationStatus.
 * Matches the RestaurantApp Android color scheme (softer tinted variants).
 */
/**
 * Get card background color based on whether the order is answered — from design tokens.
 * Matches RestaurantApp: RED (not answered) vs GREEN (answered), GRAY (cancelled).
 */
export function getConfirmationBgColor(
  order: DeepOrder
): { bg: string; border: string } {
  if (order.cancelStatus === 'cancelled') return CARD_BG.cancelled
  if (isOrderAnswered(order)) return CARD_BG.answered
  return CARD_BG.open
}

/**
 * Get urgency color class based on how long the order has been waiting — from design tokens.
 * Used for the timestamp display on KDS cards.
 */
export function getTimeUrgencyColor(timestamp: number): string {
  const minutes = Math.floor((Date.now() - timestamp) / 60000)
  return getUrgencyClass(minutes)
}

/**
 * Get card accent border class based on answered status — from design tokens.
 * Used for the thick left border on KDS cards.
 */
export function getCardAccentBorder(order: DeepOrder): string {
  if (order.cancelStatus === 'cancelled') return CARD_ACCENT.cancelled
  if (isOrderAnswered(order)) return CARD_ACCENT.answered
  return CARD_ACCENT.open
}

/**
 * Determine if an order has been "answered" — matching the RestaurantApp logic exactly.
 *
 * RestaurantApp (OrdersRecyclerViewAdapter.java lines 112-134):
 *   if confirmationEnabled:
 *     PENDING → RED (not answered), REPLIED/CONFIRMED/DECLINED → not RED (answered)
 *   else:
 *     lastStatusUpdate == date → RED (new), lastStatusUpdate != date → GREEN (answered)
 *
 * Both modes share the same concept: has anyone acted on this order yet?
 * We use lastStatusUpdate !== date as the universal check — it works for both modes
 * because confirming/replying/declining all update lastStatusUpdate.
 */
export function isOrderAnswered(order: DeepOrder): boolean {
  return order.lastStatusUpdate !== order.date
}

/**
 * Group orders into kanban columns: "not answered" vs "answered".
 * Matches the RestaurantApp RED vs GREEN distinction.
 */
export function groupOrdersByAnswered(orders: DeepOrder[]): {
  open: DeepOrder[]
  answered: DeepOrder[]
} {
  const open: DeepOrder[] = []
  const answered: DeepOrder[] = []

  for (const order of orders) {
    if (order.cancelStatus === 'cancelled') continue

    if (isOrderAnswered(order)) {
      answered.push(order)
    } else {
      open.push(order)
    }
  }

  return { open, answered }
}

/**
 * Check if an order requires confirmation action
 */
export function requiresConfirmation(order: DeepOrder): boolean {
  return order.confirmationStatus === 'pending' && order.cancelStatus !== 'cancelled'
}

/**
 * Get status badge color classes — from design tokens
 */
export function getStatusColor(status: string, cancelStatus?: CancelStatus): StatusStyle {
  return getOrderStatusStyle(status, cancelStatus === 'cancelled')
}

/**
 * Get payment status color classes — from design tokens
 */
export function getPaymentStatusColor(paymentStatus: string): PaymentStyle {
  return getPaymentStyle(paymentStatus)
}

/**
 * Get order type icon and color — from design tokens
 */
export function getOrderTypeInfo(type: OrderType): {
  icon: 'truck' | 'shopping-bag' | 'utensils'
  color: string
} {
  const iconMap: Record<OrderType, 'truck' | 'shopping-bag' | 'utensils'> = {
    delivery: 'truck',
    pickup: 'shopping-bag',
    dineIn: 'utensils',
  }
  return {
    icon: iconMap[type],
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Record key may not exist at runtime
    color: ORDER_TYPE_STYLES[type]?.color ?? 'text-status-neutral',
  }
}

/**
 * Get the current locale for date/time formatting
 */
function getLocale(): string {
  const lang = i18n.language.substring(0, 2) || 'de'
  switch (lang) {
    case 'ar': return 'ar-SA'
    case 'es': return 'es-ES'
    case 'en': return 'en-GB'
    default: return 'de-DE'
  }
}

/**
 * Format a timestamp to time only (e.g., "14:30")
 */
export function formatTime(timestamp: number): string {
  const date = new Date(timestamp)
  return date.toLocaleTimeString(getLocale(), {
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Format a timestamp to date and time (e.g., "26.12.2025 14:30")
 */
export function formatDateTime(timestamp: number): string {
  const date = new Date(timestamp)
  return date.toLocaleString(getLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Format a timestamp to relative time (e.g., "5 min ago", "2h ago")
 */
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now()
  const diffMs = now - timestamp
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMins / 60)

  if (diffMins < 1) return i18n.t('orders:time.justNow', 'Just now')
  if (diffMins < 60) return i18n.t('orders:time.minutesAgo', '{{count}} min ago', { count: diffMins })
  if (diffHours < 24) return i18n.t('orders:time.hoursAgo', '{{count}}h ago', { count: diffHours })
  return formatDateTime(timestamp)
}

/**
 * Format a date for display in the date navigator (e.g., "Thu, Dec 26")
 */
export function formatDateShort(date: Date): string {
  return date.toLocaleDateString(getLocale(), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format a date as ISO string for API (YYYY-MM-DD)
 */
export function formatDateForApi(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Parse an ISO date string to Date object
 */
export function parseApiDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number)
  return new Date(year, month - 1, day)
}

/**
 * Check if a date is today
 */
export function isToday(date: Date): boolean {
  const today = new Date()
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

/**
 * Get yesterday, today, tomorrow dates
 */
export function getAdjacentDates(currentDate: Date): {
  prev: Date
  next: Date
} {
  const prev = new Date(currentDate)
  prev.setDate(prev.getDate() - 1)

  const next = new Date(currentDate)
  next.setDate(next.getDate() + 1)

  return { prev, next }
}

/**
 * Calculate order subtotal (sum of all items before order-level discounts)
 */
export function calculateSubtotal(order: DeepOrder): number {
  return order.orderItems.reduce((sum, item) => {
    return sum + item.discountPrice * (item.quantity ?? 1)
  }, 0)
}

/**
 * Calculate total order-level discount amount.
 * Matches RestaurantApp Order.discountValue(): getCartItemsDiscount() - discountPrice.
 */
export function calculateTotalDiscount(order: DeepOrder): number {
  return Math.max(0, calculateSubtotal(order) - order.discountPrice)
}

/**
 * Calculate voucher discount amount
 */
export function calculateVoucherDiscount(order: DeepOrder): number {
  if (!order.vouchersUsed?.length) return 0
  return order.vouchersUsed.reduce((sum, v) => sum + v.discount, 0)
}

/**
 * Calculate final total.
 * discountPrice is BEFORE voucher deductions — vouchers are stored separately
 * and must be subtracted here (matching RestaurantApp behaviour).
 */
export function calculateTotal(order: DeepOrder): number {
  return order.discountPrice - calculateVoucherDiscount(order) + order.deliveryFee + order.tipsFee
}

/**
 * Get payment display label for kitchen use.
 * Shows what the kitchen needs to know: is money collected or not?
 * - Cash → "Bar" (need to collect money)
 * - Paid online → "Bezahlt" (money already collected)
 * - PayPal.me → "PayPal.me" (need to verify)
 */
export function getPaymentStatusLabel(paymentStatus: string): string {
  switch (paymentStatus.toLowerCase()) {
    case 'cash':
      return i18n.t('orders:payment.cashType', 'Bar')
    case 'paypal_success':
    case 'wirecard_success':
    case 'cc_success':
    case 'stripe_success':
      return i18n.t('orders:payment.paid', 'Bezahlt')
    case 'paypalme_new':
      return 'PayPal.me'
    case 'pending':
      return i18n.t('orders:payment.pending', 'Ausstehend')
    default:
      return paymentStatus
  }
}

/**
 * Get payment status label (matches RestaurantApp getPaymentStatusForUI).
 * Shows WHETHER the customer paid: Bezahlt, Noch nicht bezahlt, Zahlung prüfen
 */
export function getPaymentPaidLabel(paymentStatus: string): string {
  switch (paymentStatus.toLowerCase()) {
    case 'cash':
      return i18n.t('orders:payment.notYetPaid', 'Noch nicht bezahlt')
    case 'paypal_success':
    case 'wirecard_success':
    case 'cc_success':
    case 'stripe_success':
      return i18n.t('orders:payment.paid', 'Bezahlt')
    case 'paypalme_new':
      return i18n.t('orders:payment.checkPayment', 'Zahlung prüfen')
    case 'pending':
      return i18n.t('orders:payment.pending', 'Pending')
    default:
      return paymentStatus
  }
}

/**
 * Get all possible next statuses for a given status
 */
export function getNextStatuses(currentStatus: string): string[] {
  const statusFlow: Record<string, string[]> = {
    pending: ['received', 'confirmed'],
    received: ['confirmed', 'preparing'],
    confirmed: ['preparing', 'ready'],
    preparing: ['ready'],
    ready: ['completed'],
    completed: [],
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Record key may not exist at runtime
  return statusFlow[currentStatus.toLowerCase()] || []
}

/**
 * Sort orders by date (newest first) and status priority
 */
export function sortOrders(orders: DeepOrder[]): DeepOrder[] {
  const statusPriority: Record<string, number> = {
    pending: 0,
    received: 1,
    confirmed: 2,
    preparing: 3,
    ready: 4,
    completed: 5,
  }

  return [...orders].sort((a, b) => {
    // Cancelled orders go to the end
    if (a.cancelStatus === 'cancelled' && b.cancelStatus !== 'cancelled') return 1
    if (b.cancelStatus === 'cancelled' && a.cancelStatus !== 'cancelled') return -1

    // Then by status priority (pending first)
    const priorityA = statusPriority[a.status.toLowerCase()] ?? 99
    const priorityB = statusPriority[b.status.toLowerCase()] ?? 99
    if (priorityA !== priorityB) return priorityA - priorityB

    // Then by date (newest first)
    return b.date - a.date
  })
}

/**
 * Count orders by status
 */
export function countOrdersByStatus(orders: DeepOrder[]): Record<string, number> {
  const counts: Record<string, number> = {
    all: orders.length,
    pending: 0,
    active: 0, // received + confirmed + preparing + ready
    completed: 0,
    cancelled: 0,
  }

  for (const order of orders) {
    if (order.cancelStatus === 'cancelled') {
      counts.cancelled++
    } else if (order.status.toLowerCase() === 'pending') {
      counts.pending++
    } else if (order.status.toLowerCase() === 'completed') {
      counts.completed++
    } else {
      counts.active++
    }
  }

  return counts
}
